/**
 * Vendor-specific formatters.
 *
 * The shared bits (`initialsFor`, `avatarClassFor`) live in
 * `features/master-list/lib/avatar-palette.ts`. Anything strictly
 * vendor-shape-specific stays here.
 */

// Re-export the shared helpers so existing import paths stay short.
export { initialsFor, avatarClassFor } from "../../master-list/lib/avatar-palette.ts";

/**
 * GSTIN positions 3–12 carry the PAN (10 chars). Returns null on malformed
 * input. Same shape as the customer helper — could move to `master-list`
 * if a third vertical needs it.
 */
export function panFromGstin(gstin: string | null | undefined): string | null {
  if (!gstin || gstin.length !== 15) return null;
  return gstin.slice(2, 12);
}

/**
 * "Vendor since Mar 2026" — used on the detail page chip. Mirrors the
 * prototype's `head-tagline` text.
 */
export function vendorSince(createdAtIso: string): string {
  const d = new Date(createdAtIso);
  return d.toLocaleDateString("en-IN", { month: "short", year: "numeric" });
}

/**
 * Compute the cooling-off remaining (in human-readable form) given
 * `bankDetailsChangedAt`. Returns `null` if no change has occurred or the
 * window has passed.
 *
 * Used on the vendor detail page's bank-details card to render
 * "Available 17:42 IST" style copy.
 *
 * @see ADR-0008 / system-decisions §14
 */
export function coolingOffStatus(bankChangedAtIso: string | null): {
  inCoolingOff: boolean;
  availableAt: Date | null;
} {
  if (!bankChangedAtIso) return { inCoolingOff: false, availableAt: null };
  const changedAt = new Date(bankChangedAtIso);
  const COOLING_OFF_MS = 24 * 60 * 60 * 1000;
  const availableAt = new Date(changedAt.getTime() + COOLING_OFF_MS);
  return {
    inCoolingOff: availableAt.getTime() > Date.now(),
    availableAt,
  };
}
